import { API_URL } from '@/_libs/constants';
import { base64toBlob } from '@/_utils/base64';
import { signedOutWithinLastNSeconds } from '@/_utils/auth';
import { SORABAN_AUTH_TOKEN } from '@/_constants/cookies';
import {
  getLocalStorageItem,
  setLocalStorageItem,
  removeLocalStorageItem,
} from '@/_utils/localstorage';
import { downloadBlob } from '@/_utils/files';
import { refreshSorabanToken } from './auth';

const IS_DEVELOPMENT: boolean = process.env.NODE_ENV === 'development';

const handleUnauthorizedResponse = async (response: any) => {
  if (!signedOutWithinLastNSeconds(10)) {
    setLocalStorageItem('signOut', Date.now().toString()); // triggers refresh in all tabs
  }

  removeLocalStorageItem(SORABAN_AUTH_TOKEN);

  setTimeout(() => {
    if (IS_DEVELOPMENT) {
      console.debug('⚠️ hard refresh from handleUnauthorizedResponse');
    }

    window.location.href = '/';
  }, 0);

  throw new Error(response as any);
};

// Use this function to call the API
export const _callBackend = async (
  url: string,
  method: 'GET' | 'POST' | 'DELETE' | 'PUT' | 'PATCH' = 'GET',
  data?: FormData | object | null,
  authToken?: string,
  options?: {
    returnResponseAsIs?: boolean;
    timeout?: number;
  },
): Promise<any> => {
  // TODO: Refactoring needed for sync/async type safety. See SOR-13473.
  try {
    const isGet: boolean = method === 'GET';
    const { timeout, ...restOptions } = options || {};

    const makeCall = async (tokenOverride: string | null = null) => {
      const controller = timeout ? new AbortController() : null;
      const timeoutId =
        timeout && controller
          ? setTimeout(() => controller.abort(), timeout)
          : null;

      try {
        const response = await fetch(`${API_URL}${url}`, {
          headers: {
            ...(data instanceof FormData
              ? {}
              : { 'Content-Type': 'application/json' }),
            ...(authToken ? { Authorization: tokenOverride || authToken } : {}),
          },
          method,
          ...(!isGet
            ? { body: data instanceof FormData ? data : JSON.stringify(data) }
            : {}),
          ...(controller ? { signal: controller.signal } : {}),
        });

        if (timeoutId) clearTimeout(timeoutId);
        return response;
      } catch (error) {
        if (timeoutId) clearTimeout(timeoutId);
        if (controller && (error as Error).name === 'AbortError') {
          throw new Error('Request timeout');
        }
        throw error;
      }
    };

    let response = await makeCall();

    // If we get a 401 from this specific endpoint, then we actually want to go through a different flow (handled via SignIn component)
    const isTokenExchangeUrl = url.includes(
      '/sessions/exchange_for_firebase_token',
    );

    // If this endpoint returns a 401, the user isn't logged in in the first place,
    // so we don't want to "log them out", unnecessarily causing a hard page refresh.
    const isVerifyVerificationCodeUrl = url.includes(
      '/sessions/verify_verification_code',
    );

    let newToken;

    // The first time we get a 401, we'll try and refresh the token to make the call again in case it got out of sync across tabs.
    if (!isTokenExchangeUrl && response.status === 401) {
      if (authToken?.startsWith('Bearer ')) {
        // For WorkOS tokens, try localStorage sync first
        const storedToken = getLocalStorageItem(SORABAN_AUTH_TOKEN);

        if (storedToken && storedToken !== authToken) {
          newToken = storedToken;
          response = await makeCall(newToken);
        } else {
          // If that fails, try to refresh the Soraban token
          try {
            const sorabanTokenWithoutBearer = authToken.replace('Bearer ', '');
            const refreshResponse = await refreshSorabanToken({
              sorabanToken: sorabanTokenWithoutBearer,
            });

            if (refreshResponse.success && refreshResponse.auth_token) {
              newToken = `Bearer ${refreshResponse.auth_token}`;
              response = await makeCall(newToken);

              // Update localStorage so other tabs get the new token
              setLocalStorageItem(SORABAN_AUTH_TOKEN, newToken);
            }
          } catch (refreshError) {
            console.error('Failed to refresh Soraban token:', refreshError);
            // Let the 401 handling below proceed if refresh fails
          }
        }
      }
    }

    if (
      !isTokenExchangeUrl &&
      !isVerifyVerificationCodeUrl &&
      response.status === 401
    ) {
      await handleUnauthorizedResponse(response);
    } else if (
      response.status >= 200 &&
      response.status < 300 &&
      newToken &&
      authToken !== newToken
    ) {
      // The UserProvider listens for changes to SORABAN_AUTH_TOKEN in localStorage. And if it changes, we'll update the token in that provider's state.
      try {
        let tokenReplacement;

        if (newToken) {
          tokenReplacement = newToken;
        } else if (authToken) {
          tokenReplacement = authToken;
        }

        if (
          !!tokenReplacement &&
          tokenReplacement !== getLocalStorageItem(SORABAN_AUTH_TOKEN)
        ) {
          removeLocalStorageItem(SORABAN_AUTH_TOKEN);
          setLocalStorageItem(SORABAN_AUTH_TOKEN, tokenReplacement);
        }
      } catch (error) {
        console.error(error);
      }
    }

    if (restOptions?.returnResponseAsIs || response?.status === 204) {
      return response;
    }

    return response.json();
  } catch (error) {
    console.error(error);
  }
};

// Use this function to start downloading a file
export const _callFile = async (
  url: string,
  method: 'GET' | 'POST' = 'GET',
  extension: string,
  fileName: string,
  authToken?: string,
  data?: object | null,
): Promise<any> => {
  try {
    const isGet: boolean = method === 'GET';

    const response = await fetch(`${API_URL}${url}`, {
      headers: {
        'Content-Type': 'application/json',
        ...(authToken ? { Authorization: authToken } : {}),
      },
      method,
      ...(!isGet ? { body: JSON.stringify(data) } : {}),
    });

    const base64String: string = await response.text();
    const blob: Blob = base64toBlob(base64String, extension);

    downloadBlob(blob, fileName);
  } catch (error) {
    console.error(error);
  }
};

export const _downloadBlob = async ({
  url,
  fileName,
}: {
  url: string;
  fileName: string;
}): Promise<void> => {
  try {
    const response: Response = await fetch(url);

    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    const blob: Blob = await response.blob();
    downloadBlob(blob, fileName);
  } catch (error) {
    try {
      const a = document.createElement('a');

      a.href = url;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';

      document.body.appendChild(a);
      a.click();

      setTimeout(() => a.remove(), 0);
    } catch (e) {
      throw new Error(`Failed to download file: ${error}`);
    }
  }
};
